import { z } from 'zod';
import { LegalDocumentPurpose } from '@pillar-portal/database/dist/frontend';
import type {
    CreateLegalDocumentAssignmentRequest,
    LegalDocumentMutableStatus,
} from './legal-documents.types';
import {
    CreateLegalDocumentAssignmentRequestSchema,
    LegalDocumentMutableStatusSchema,
} from './schemas/legal-documents.schemas';
import { NonEmptyStringSchema } from './schemas/common.schemas';

/**
 * Internal-admin contracts for checking the effect of a legal-agreement change
 * before making it, and for the health of the legal configuration.
 */

// Discriminator: action
export type LegalAgreementImpactRequest =
    | {
          action: 'assign';
          assignment: CreateLegalDocumentAssignmentRequest;
      }
    | {
          action: 'revoke';
          legal_document_assignment_uuid: string;
          /** A user agreement assigned as the platform default, from now, in the same step. */
          replacement_legal_document_uuid?: string;
      }
    | {
          action: 'change_status';
          legal_document_uuid: string;
          status: LegalDocumentMutableStatus;
      };

export interface LegalAgreementDocumentReference {
    legal_document_uuid: string;
    legal_document_title: string;
    legal_document_version: string;
}

/**
 * One way the applicable agreements change, and for whom. For a user agreement
 * the change applies per title; for an Organization agreement per document.
 */
export interface LegalAgreementImpactChange {
    legal_document_purpose: LegalDocumentPurpose;
    legal_document_title: string;
    /** What applied before the change; null when nothing with this title did. */
    previous_legal_document: LegalAgreementDocumentReference | null;
    /** What applies after the change; null when nothing with this title will. */
    next_legal_document: LegalAgreementDocumentReference | null;
    organization_count: number;
    /** Up to five of the affected Organizations, alphabetically. */
    sample_organization_names: string[];
    /** Users of those Organizations who have not yet accepted the next user agreement. */
    users_to_accept: number;
    /** Those Organizations that have not yet signed the next Organization agreement. */
    organizations_to_sign: number;
}

export interface LegalAgreementImpact {
    /** The instant the change is evaluated at: now, or a later effective start. */
    evaluated_at: string;
    changes: LegalAgreementImpactChange[];
    /** True when the change leaves no platform-default user agreement in force. */
    leaves_no_platform_default: boolean;
}

// Discriminator: code
export type LegalConfigurationHealthWarning =
    | {
          /** No platform-default user agreement is in force. */
          code: 'no_platform_default';
      }
    | {
          /** The platform default for a title stops being in force with no successor. */
          code: 'platform_default_ending';
          legal_document_title: string;
          ends_at: string;
      }
    | {
          /** An assignment in force names a document that is not active or not in force. */
          code: 'assigned_document_unavailable';
          legal_document_assignment_uuid: string;
          legal_document: LegalAgreementDocumentReference;
          organization_name: string | null;
      };

export interface LegalConfigurationHealth {
    evaluated_at: string;
    warnings: LegalConfigurationHealthWarning[];
}

const LegalAgreementDocumentReferenceSchema: z.ZodType<LegalAgreementDocumentReference> = z
    .object({
        legal_document_uuid: z.string().uuid(),
        legal_document_title: NonEmptyStringSchema,
        legal_document_version: NonEmptyStringSchema,
    })
    .strict();

export const LegalAgreementImpactRequestSchema: z.ZodType<LegalAgreementImpactRequest> =
    z.discriminatedUnion('action', [
        z
            .object({
                action: z.literal('assign'),
                assignment: CreateLegalDocumentAssignmentRequestSchema,
            })
            .strict(),
        z
            .object({
                action: z.literal('revoke'),
                legal_document_assignment_uuid: z.string().uuid(),
                replacement_legal_document_uuid: z.string().uuid().optional(),
            })
            .strict(),
        z
            .object({
                action: z.literal('change_status'),
                legal_document_uuid: z.string().uuid(),
                status: LegalDocumentMutableStatusSchema,
            })
            .strict(),
    ]);

const nonNegativeCountSchema = z.number().int().nonnegative();

export const LegalAgreementImpactSchema: z.ZodType<LegalAgreementImpact> = z
    .object({
        evaluated_at: z.string().datetime(),
        changes: z.array(
            z
                .object({
                    legal_document_purpose: z.enum(LegalDocumentPurpose),
                    legal_document_title: NonEmptyStringSchema,
                    previous_legal_document: LegalAgreementDocumentReferenceSchema.nullable(),
                    next_legal_document: LegalAgreementDocumentReferenceSchema.nullable(),
                    organization_count: nonNegativeCountSchema,
                    sample_organization_names: z.array(z.string()).max(5),
                    users_to_accept: nonNegativeCountSchema,
                    organizations_to_sign: nonNegativeCountSchema,
                })
                .strict()
        ),
        leaves_no_platform_default: z.boolean(),
    })
    .strict();

export const LegalConfigurationHealthSchema: z.ZodType<LegalConfigurationHealth> = z
    .object({
        evaluated_at: z.string().datetime(),
        warnings: z.array(
            z.discriminatedUnion('code', [
                z.object({ code: z.literal('no_platform_default') }).strict(),
                z
                    .object({
                        code: z.literal('platform_default_ending'),
                        legal_document_title: NonEmptyStringSchema,
                        ends_at: z.string().datetime(),
                    })
                    .strict(),
                z
                    .object({
                        code: z.literal('assigned_document_unavailable'),
                        legal_document_assignment_uuid: z.string().uuid(),
                        legal_document: LegalAgreementDocumentReferenceSchema,
                        organization_name: z.string().nullable(),
                    })
                    .strict(),
            ])
        ),
    })
    .strict();
